import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { decideMfaGate, type MfaGateDecision, type PatientDoc } from '@usacsmideo/types';
import { getAuthAssurance } from '../utils/mfa/authAssurance';
import { getServerTimeMs } from '../utils/mfa/serverTime';
import { recordGateDecision } from '../utils/mfa/gateDecisionTrail';

/**
 * The PHI-access routing decision — CU-86ajwpnek §9, ported from
 * `mideo-health-app`'s `useMfaRouting`.
 *
 * ⚠️ THERE IS DELIBERATELY NO `if (!aal2 && enforced)` IN THIS FILE. That rule
 * lives in `decideMfaGate` (now in `@usacsmideo/types`, shared by three
 * consumers). This hook GATHERS inputs and WIDENS the answer; it never
 * re-derives it. Two implementations of a PHI gate is how one silently drifts
 * open.
 *
 * ⚠️ THIS GATE IS ARMED AND LIVE — the cutover sweep (CU-86ajnb1qh) ran
 * 2026-09-01 and stamps `mfa.enforced: true`, so `decideMfaGate` branch 2 no
 * longer returns "pass" for a swept patient. The decision this hook gathers
 * inputs for now denies PHI for real.
 *
 * ⚠️ Branch 2 still matters for an UNSWEPT patient and must not be removed.
 * "Inert" described the population, never the code.
 *
 * Do not flip enforcement from the client — that is the sweep's job.
 */

/**
 * `MfaGateDecision` plus the state the DECISION cannot express: we have not
 * finished asking yet.
 *
 * ⚠️ `resolving` is fail-closed. It never renders PHI "until we know" — a gate
 * that flashes PHI for one frame before deciding is not a gate.
 */
export type MfaRoutingStatus = MfaGateDecision | 'resolving';

export interface UseMfaRouting {
  status: MfaRoutingStatus;
  /** How the session reached AAL2, when it did. Telemetry and copy only. */
  method: 'passkey' | 'mfa' | null;
  /** Re-decide from the TOKEN — call after enrollment completes. */
  refresh: () => void;
}

export function useMfaRouting(patientDoc: PatientDoc | undefined): UseMfaRouting {
  const [status, setStatus] = useState<MfaRoutingStatus>('resolving');
  const [method, setMethod] = useState<'passkey' | 'mfa' | null>(null);
  const [nonce, setNonce] = useState(0);

  /** Bumping the nonce re-runs the decision. */
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const enforced = patientDoc?.mfa?.enforced;
  const exception = patientDoc?.mfa?.exception;
  const enrollmentState = patientDoc?.mfa?.enrollmentState;
  /**
   * ⚠️ HOISTED TO A PRIMITIVE, like every other dependency above. The Firestore
   * listener re-emits a NEW OBJECT IDENTITY on each snapshot, so depending on
   * `patientDoc` itself would re-run this effect — and its forced token refresh
   * — continuously. Used only to key the gate-decision trail's deduplication.
   */
  const uid = patientDoc?.uid;

  // Guards a late resolve from a superseded run overwriting a newer answer.
  const runIdRef = useRef(0);

  useEffect(() => {
    const runId = ++runIdRef.current;
    let cancelled = false;

    const decide = async () => {
      // ⚠️ forceRefresh: true. A cached token would decide on STALE assurance —
      // which is exactly wrong immediately after enrollment, the moment this
      // most needs to be right.
      const assurance = await getAuthAssurance(true);

      // ⚠️ Only pay for the server-time round trip when it can change the
      // answer. That is precisely the precondition for reaching branch 3 of
      // `decideMfaGate`, so this optimisation is coupled to the branch order:
      // an aal2 session (branch 1) and a pre-cutover session (branch 2) both
      // return before the clock is ever consulted.
      const needsServerTime = !assurance.aal2 && !!enforced && !!exception;
      const serverTimeMs = needsServerTime ? await getServerTimeMs() : null;

      if (cancelled || runId !== runIdRef.current) return;

      const decision = decideMfaGate({
        aal2: assurance.aal2,
        enrollmentState,
        enforced,
        exception,
        serverTimeMs,
      });

      if (decision !== 'pass') {
        Sentry.addBreadcrumb({
          category: 'mfa-gate',
          level: 'info',
          message: `mfa gate: ${decision}`,
          data: {
            aal2: assurance.aal2,
            method: assurance.method,
            enforced: !!enforced,
            enrollmentState: enrollmentState ?? '(absent)',
            hadException: !!exception,
            // ⚠️ The BOOLEAN of clock knowledge, never the clock value — it
            // distinguishes "the exception expired" from "we were offline",
            // which are different findings with the same outcome.
            serverTimeKnown: serverTimeMs !== null,
          },
        });

        /**
         * ⚠️ THE DURABLE RECORD — CU-86ajxvg7y.
         *
         * The breadcrumb above is KEPT (it is free, and it preserves per-render
         * ordering inside any event that IS captured), but a breadcrumb only
         * surfaces attached to a captured event. When the gate decides
         * unexpectedly and nothing throws, it is written to nowhere — which is
         * exactly the case that cost a debugging session, and exactly the
         * post-cutover support question "why was I gated?".
         *
         * ⚠️ DEDUPLICATED ACROSS BOTH `useMfaRouting` CALL SITES via module
         * state inside `recordGateDecision`. This hook runs twice per gated
         * view (here and in `$patientId.tsx`), so per-hook memory would
         * deduplicate nothing.
         */
        recordGateDecision(uid, decision, {
          aal2: assurance.aal2,
          method: assurance.method,
          enforced: !!enforced,
          enrollmentState,
          hadException: !!exception,
          serverTimeKnown: serverTimeMs !== null,
        });
      }

      setMethod(assurance.method);
      setStatus(decision);
    };

    void decide();
    return () => {
      cancelled = true;
    };
  }, [enforced, exception, enrollmentState, nonce, uid]);

  return { status, method, refresh };
}
